<template>
  <div
    class="flex-1 flex flex-col"
  >
    <GMPropertyHeader
      v-if="currentProperty"
      :property-name="currentProperty?.Name || ''"
      :orb-text="currentProperty.IsMisRegCompleted ? t('MISREG_PROPERTY') : ''"
      :sub-sections="[t('NEW_TYPE', { type: t(contractLabel) })]"
      :property-path="`/customers/${route.params.customerId}/${route.params.propertyId}`"
      class="shadow-inner"
    >
      <template #badge>
        <GMBadge
          v-if="currentProperty.IsMisRegCompleted"
          color="blue"
          icon-only
          icon="shield-check"
        >
          <span class="sr-only">
            {{ t('MISREG_PROPERTY') }}
          </span>
        </GMBadge>
        <GMBadge
          v-if="currentProperty.Fsc"
          icon="fsc"
          icon-only
        >
          <span class="sr-only">
            FSC
          </span>
        </GMBadge>
        <HasPowerOfAttorneysBadge :property-id="currentProperty.Id" icon-only />
      </template>

      <GMMenuButton v-if="currentStep" align="right" is-nav>
        <template #trigger="{ isVisible }">
          <GMButton
            :icon-after="isVisible ? 'chevron-up' : 'chevron-down'"
            class="uppercase tracking-wider"
            icon-only
          >
            <span class="sr-only sm:not-sr-only sm:mr-2 dark:text-blue-300">
              {{ t(currentStep?.Title || '') }}
            </span>
          </GMButton>
        </template>
        <GMMenuButtonOption
          v-for="step in steps"
          :key="`menuoption_${step.Page}`"
          :is-nav-active="step.Page === currentStep.Page"
          :disabled="step.Step > stepsCompletedUpTo"
          @click="goToPage(step.Page)"
        >
          {{ t(step?.Title || '') }}
        </GMMenuButtonOption>
      </GMMenuButton>
    </GMPropertyHeader>

    <NuxtPage
      v-if="contract && !isPendingSaveContract"
      @hidenav="showNav = false"
      @shownav="showNav = true"
      @go-next="nextStep ? goToPage(nextStep.Page) : noop()"
      @go-back="previousStep ? goToPage(previousStep.Page) : noop()"
    />

    <div v-else-if="isLoadingContractReference || isPendingSaveContract" class="flex flex-1 items-center justify-center w-full space-x-2">
        <div v-if="isLoadingContractReference" class="flex flex-1 items-center justify-center w-full space-x-2">
          <GMLoader size="small"/>
          <AnimatedText  text="Laster kontrakt..." />
        </div>
        <div v-else-if="isPendingSaveContract" class="flex-1 justify-center flex flex-col items-center -mt-8">
          <GMLoader size="large" class="text-blue-500 mx-auto">
            <div class="flex items-end">
              <GMIcon
                  name="document"
                  class="w-10 h-10 text-blue-300 dark:text-blue-600"
              />
              <GMIcon
                  name="arrow-right"
                  class="-ml-5 w-8 h-8 text-blue-500 dark:text-blue-400"
              />
            </div>
          </GMLoader>
          <div class="text-center text-blue-gray-700 dark:text-blue-gray-300">
            <AnimatedText
                v-if="contract?.Status === CONTRACT_STATES_DRAFT"
                class="text-lg"
                :text="t('SAVING_AND_SYNCHRONIZING_DRAFT')"
            />
            <AnimatedText v-else class="text-lg" :text="isSigned ? t('FINISHING_CONTRACT') : t('SAVING_CONTRACT')" />
            <p class="text-sm opacity-70"> {{t('PLEASE_WAIT') }} </p>
          </div>
        </div>
    </div>

    <div
      v-if="!isPendingSaveContract && hasContractData && currentStep && showNav"
      class="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 w-full fixed bottom-0"
    >
      <div class="max-w-4xl m-auto px-4">
        <div class="flex w-full">
          <div class="w-1/3 text-left">
            <GMButton
              v-if="previousStep"
              tertiary
              icon-before="chevron-left"
              class="!py-6 uppercase tracking-wider"
              @click="goToPage(previousStep.Page)"
            >
              {{ t(previousStep?.Title || '') }}
            </GMButton>
          </div>
          <div class="w-1/3 text-center flex items-center justify-center dark:text-gray-300">
            <span class="text-lg">{{ currentStep.Step }} <span class="text-gray-300 dark:text-gray-500">/</span> {{ steps.length }}</span>
          </div>
          <div class="w-1/3 text-right">
            <GMButton
              v-if="nextStep"
              tertiary
              icon-after="chevron-right"
              class="!py-6 uppercase tracking-wider"
              :disabled="!valid"
              @click="goToPage(nextStep.Page)"
            >
              {{ t(nextStep?.Title || '') }}
            </GMButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  GMBadge,
  GMButton,
  GMIcon,
  GMLoader,
  GMMenuButton,
  GMMenuButtonOption,
  GMPropertyHeader,
  notify
} from '@gm/components'
import type {ContractTypeStep, FullContract} from '~/models/contract'
import {useDraftNotice} from '~/helpers/saveDraftNotice'
import {useContractStore} from '~/stores/contract'
import {
  getContractStateLabel,
  getContractTypeLabel,
  getContractTypeSteps,
  getDefaultContractTypeStepsFilters,
  hasUnsavedChanges,
  isDraft
} from '~/helpers/contracts'
import {isPageValid} from '~/helpers/validations'
import {useHeaderStore} from '~/use/ui/header'
import HasPowerOfAttorneysBadge from '~/components/Customers/HasPowerOfAttorneysBadge.vue'
import {CONTRACT_STATES_DRAFT, CONTRACT_STATES_SIGNATURE_REJECTED, CONTRACT_STATES_SIGNED} from '~/constants/contract'
import {useOnline} from '@vueuse/core';
import {useContractService} from "~/services/contractService";
import {useIsFetching} from "@tanstack/vue-query";
import {isTempContract} from "~/helpers/contractHelper";
import {useIsSavingContract} from "~/composables/useIsSavingContract";
import {CONTRACT_SIGNATURE_TYPE_CODE_BANKID} from "~/constants/contracts";
import {useCustomerService} from "~/services/customerService";
import {useProperty} from "~/composables/useProperty";
import {usePowerOfAttorneysService} from "~/services/powerOfAttorneysService";
import {useQuestionService} from "~/services/questionService";
import {queryClient} from "~/plugins/vue-query";

export default defineComponent({
  name: 'EditContractPage',
  components: {
    GMLoader,
    GMBadge,
    GMButton,
    GMMenuButton,
    GMMenuButtonOption,
    GMPropertyHeader,
    GMIcon,
    HasPowerOfAttorneysBadge,
  },
  setup () {
    const { t } = useTranslation()
    const route = useRoute()
    const router = useRouter()
    const customerId = route.params.customerId as string
    const propertyId = route.params.propertyId as string
    const { getPropertyRightToSignQuery } = usePowerOfAttorneysService()
    const { data: currentPropertyPowerOfAttorneys, isFetching: isLoadingPowerOfAttorneys} = getPropertyRightToSignQuery(propertyId)
    const { property: currentProperty } = useProperty(customerId, propertyId)
    const contractId = route.params.contractId as string
    const { customerQuery } = useCustomerService()
    const {data: currentCustomer} = customerQuery(customerId)
    const { contractQuery, saveContractMutation } = useContractService()
    const { mutate: saveContract } = saveContractMutation()
    const { data: contractReference, isLoading: isLoadingContractReference } = contractQuery(contractId)
    const isPendingSaveContract = useIsSavingContract(contractId)
    const contractStore = useContractStore()
    const hasContractData = computed(() => contractReference.value && JSON.stringify(contractReference.value) !== '{}')
    // Must check this before calling contractStore.getContractDraftById
    watch([hasContractData, isLoadingContractReference, isPendingSaveContract], () => {
      if (!isLoadingContractReference.value && !hasContractData.value) {
        notify(t('error.EMPTY_CURRENT_CONTRACT').toString(), {
          type: 'error'
        })
        router.push(`/customers/${customerId}/${propertyId}`)
      }

      if (!!contractReference.value && !isPendingSaveContract.value && !contractStore.contractDraftExists(contractId)) {
        contractStore.addContractDraft(contractReference.value)
      }
    }, { immediate: true })

    const contract = contractStore.getContractDraftById(contractId, contractReference.value)

    const { setHeaderLink } = useHeaderStore()
    const isOnline = useOnline()

    const isSigned = computed(
        () =>
            contract.value?.SignatureTypeCode !==
            CONTRACT_SIGNATURE_TYPE_CODE_BANKID &&
            (contract.value?.SignatureOwner || false) &&
            contract.value?.SignatureOwner.length > 0 &&
            (contract.value?.SignatureBuyer || false) &&
            contract.value?.SignatureBuyer.length > 0
    )

    const title = computed(() => `${t(getContractTypeLabel(contract.value?.Type))} ${contract.value?.ContractNo || (t(getContractStateLabel(contract.value)) as string).toLowerCase()} | ${currentProperty.value?.Name} | ${currentCustomer.value?.Name}`)
    watch(() => title.value, () => {
      useHead({title: title.value})
    }, {immediate: true})


    const {
      isShowing,
      showDraftNotice,
      discardDraft,
      saveDraft
    } = useDraftNotice(contractId)

    const allmaJobsFetching = useIsFetching({queryKey: ['allmaJobGetAllmaJob']})
    const isFetchingAllmaJobs = computed(() => allmaJobsFetching.value > 0)
    const { questionTemplatesQuery } = useQuestionService()
    const { data: questionTemplates } = questionTemplatesQuery()
    const showNav: Ref<boolean> = ref(true)

    const contractLabel: ComputedRef<string> = computed(() => !!contract.value?.Type ? getContractTypeLabel(contract.value.Type).toLowerCase() : '')
    const steps: ComputedRef<ContractTypeStep[]> = computed(() => !!contract.value?.Type ? getContractTypeSteps(contract.value.Type, getDefaultContractTypeStepsFilters(currentCustomer.value)) : [])
    const currentPage: ComputedRef<string> = computed(() => route.name?.split('-').slice(-1)[0])
    const currentStep: ComputedRef<ContractTypeStep> = computed(() => steps.value.find(x => x.Page === currentPage.value))
    const previousStep: ComputedRef<ContractTypeStep> = computed(() => steps.value.find(x => x.Step === currentStep.value?.Step - 1) || null)
    const nextStep: ComputedRef<ContractTypeStep> = computed(() => steps.value.find(x => x.Step === currentStep.value?.Step + 1) || null)
    const stepsCompletedUpTo: ComputedRef<number> = computed(() => {
      let max = null
      steps.value.forEach((step: ContractTypeStep) => {
        if (!max && questionTemplates.value && !isPageValid(computed(() => step.Page), contract, currentPropertyPowerOfAttorneys, isLoadingPowerOfAttorneys, currentCustomer.value, isFetchingAllmaJobs, questionTemplates).value) {
          max = step.Step
        }
      })
      return max || steps.value.length
    })

    onBeforeMount(() => {
      setHeaderLink({
        Url: `/customers/${route.params.customerId}/${route.params.propertyId}/${contractId}`,
        Label: 'CONTRACT'
      })
      // Cant edit signed contracts
      if (!!contract.value && typeof contract.value.Id !== 'undefined' && !isDraft(contract.value) && contract.value?.Status && contract.value?.Status >= CONTRACT_STATES_SIGNED && contract.value.Status !== CONTRACT_STATES_SIGNATURE_REJECTED) {
        throw createError({message: 'PAGE_NOT_ALLOWED'})
      }
    })

    const noop = () => {}

    const goToPage = (page: string): void => {
      router.push(page)
    }
    const valid = computed(() => {
      return !!questionTemplates.value && isPageValid(currentPage, contract, currentPropertyPowerOfAttorneys, isLoadingPowerOfAttorneys,currentCustomer.value, isFetchingAllmaJobs, questionTemplates).value
    })

    onBeforeRouteLeave(async (to, _, next) => {
      // If redirect to login, assume user wants so save before being redirected back
      if (to.path.includes('/login')) {
        await saveDraft(contract.value)
        next()
        return
      }

      if (isOnline && !isShowing.value && hasContractData.value && contractStore.contractDraftExists(contractId) && hasUnsavedChanges(contract.value, contractReference.value)) {
        const result = await showDraftNotice(contract.value, contractReference.value as FullContract || {})

        switch (result) {
          case 'cancel': {
            next(false)
            break
          }
          case 'ignore': {
            next()
            contractStore.removeContractDraftById(contractId)
            break
          }
          case 'discard': {
            await discardDraft(contractId).then(() => {
              if (to.name === 'customers-customerId-propertyId-contractId') {
                next({
                  name: 'customers-customerId-propertyId',
                  params: {
                    customerId: currentCustomer.value.Id,
                    propertyId: propertyId,
                  }
                })
                return ''
              } else {
                next()
              }
            })
            contractStore.removeContractDraftById(contractId)
            if (isTempContract(contractId)) {
              queryClient.removeQueries({queryKey:['contractGetContract', contractId]})
            }
            break
          }
          case 'saveDraft': {
            await saveDraft(contract.value)
            next()
            queryClient.setQueryData(['contractGetContract', contractId], () => contract.value)
            contractStore.removeContractDraftById(contractId)
            if (isTempContract(contractId)) {
              queryClient.removeQueries({queryKey:['contractGetContract', contractId]})
            }
            break
          }
          case 'saveContract': {
            await discardDraft(contractId)
            next()
            setTimeout(() => {
              saveContract({contract: contract.value})
              contractStore.removeContractDraftById(contractId)
              if (isTempContract(contractId)) {
                queryClient.removeQueries({queryKey:['contractGetContract', contractId]})
              }
            }, 10)
            break
          }
          default: {
            next()
          }
        }
      } else {
        next()
        contractStore.removeContractDraftById(contractId)
      }
    })

    return {
      CONTRACT_STATES_DRAFT,
      t,
      route,
      currentProperty,
      contract,
      hasContractData,
      contractReference,
      isLoadingContractReference,
      currentCustomer,
      contractLabel,
      getContractTypeLabel,
      getContractStateLabel,
      steps,
      isPageValid,
      currentStep,
      previousStep,
      nextStep,
      showNav,
      stepsCompletedUpTo,
      valid,
      goToPage,
      noop,
      isOnline,
      isPendingSaveContract,
      isSigned,
    }
  },
  head: {
    title: ''
  }
})
</script>
